import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { alpha } from '@mui/material';

/**
 * One place for the chat surface's shared visual decisions, so the full page and the drawer are the
 * same product rather than two things that drifted. Everything resolves through theme tokens, so it
 * follows the app's light and dark palettes instead of hardcoding a second one.
 */

/** Reading measure. Long answers with tables need a column, not the full width of a monitor. */
export const COLUMN = {
  width: '100%',
  maxWidth: 760,
  mx: 'auto'
};
export const SCROLLER = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  px: {
    xs: '16px',
    sm: '24px'
  },
  py: '20px',
  // A flush-to-the-edge scrollbar on a dark panel reads as a seam; inset and dim it.
  '&::-webkit-scrollbar': {
    width: 10
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 8,
    border: '3px solid transparent',
    backgroundClip: 'content-box',
    backgroundColor: 'action.disabled'
  }
};
export const USER_BUBBLE = {
  px: '14px',
  py: '9px',
  maxWidth: '78%',
  borderRadius: '16px 16px 4px 16px',
  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.05)',
  border: 1,
  borderColor: 'divider'
};

/** Provenance, not decoration: it must never outweigh the answer it describes. */
export const TOOL_CHIP = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  px: '7px',
  fontSize: 11,
  fontWeight: 400,
  lineHeight: '20px',
  borderRadius: '6px',
  border: 1,
  borderColor: 'divider',
  color: 'text.secondary',
  bgcolor: 'transparent'
};

/**
 * One card shape for everything Livy renders inside an answer: starters, charts and proposals.
 * They were three separate literals that happened to agree, which is a coincidence rather than a
 * decision, and coincidences drift.
 */
export const CARD = {
  borderRadius: '12px',
  border: 1,
  borderColor: 'divider'
};

/**
 * A card inside an answer that is waiting on a decision.
 *
 * Proposals and engineering requests share it so "something here needs you" reads the same
 * wherever it appears, and so two asks of the same kind cannot drift into two different-looking
 * cards. Undecided is warning-toned; settled drops back to the ordinary divider.
 */
export const ACTION_CARD = settled => _objectSpread(_objectSpread({}, CARD), {}, {
  mt: '14px',
  p: '14px',
  borderColor: settled ? 'divider' : 'warning.main',
  bgcolor: 'background.paper'
});

/** The line above a card's title saying what kind of thing it is. */
export const CARD_EYEBROW = {
  fontSize: 10.5,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: '6px'
};

/** Ripples are off inside Livy (see LivyTheme), so a ButtonBase needs its own keyboard focus mark. */
export const FOCUS_RING = {
  '&.Mui-focusVisible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px'
  }
};

/** A suggestion under the opening composer: a small pill with an icon, centred and wrapping. */
export const SUGGESTION_CHIP = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: 40,
  maxWidth: '100%',
  px: '16px',
  py: '8px',
  borderRadius: '20px',
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.35,
  textAlign: 'left',
  transition: 'border-color .2s, background-color .2s, transform .2s',
  '& svg': {
    flex: '0 0 auto',
    color: 'text.secondary',
    transition: 'color .2s'
  },
  '&:hover': {
    borderColor: 'primary.light',
    bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
    transform: 'translateY(-1px)',
    '& svg': {
      color: 'primary.main'
    }
  },
  '&.Mui-focusVisible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px'
  },
  '@keyframes livyChipIn': {
    from: {
      opacity: 0,
      transform: 'translateY(6px)'
    },
    to: {
      opacity: 1,
      transform: 'none'
    }
  },
  animation: 'livyChipIn .45s cubic-bezier(.2,.8,.2,1) both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none'
  }
};

/**
 * The composer is the one control on the page, so it carries the brand: a teal gradient ring at
 * rest, a darker one while typing, and a teal arc spinning around it while Livy is thinking.
 *
 * The ring is 1.5px of the wrapper showing around the inner pill, because a border cannot hold a
 * gradient. Each state is its own layer under the pill and fades by opacity, since a gradient
 * itself cannot transition. The arc is an oversized conic gradient spun behind the pill and
 * clipped by the wrapper's rounded overflow.
 */
export const COMPOSER_RING = thinking => ({
  position: 'relative',
  overflow: 'hidden',
  isolation: 'isolate',
  p: '1.5px',
  borderRadius: '30px',
  background: theme => "linear-gradient(100deg, ".concat(theme.palette.primary.light, ", ").concat(theme.palette.primary.main, " 55%, ").concat(alpha(theme.palette.primary.light, 0.7), ")"),
  boxShadow: theme => "0 0 0 4px ".concat(alpha(theme.palette.primary.main, 0.06), ", 0 8px 28px ").concat(alpha(theme.palette.primary.dark, 0.1)),
  transition: 'box-shadow .3s ease',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    zIndex: -1,
    opacity: 0,
    transition: 'opacity .3s ease'
  },
  // Focus: the darker ring.
  '&::after': {
    inset: 0,
    background: theme => "linear-gradient(100deg, ".concat(theme.palette.primary.main, ", ").concat(theme.palette.primary.dark, " 55%, ").concat(theme.palette.primary.main, ")")
  },
  '&:focus-within': {
    boxShadow: theme => "0 0 0 4px ".concat(alpha(theme.palette.primary.main, 0.16), ", 0 8px 28px ").concat(alpha(theme.palette.primary.dark, 0.16)),
    '&::after': {
      opacity: thinking ? 0 : 1
    }
  },
  // Thinking: a quiet base with one bright arc going round.
  '&::before': {
    top: '50%',
    left: '50%',
    width: '200%',
    aspectRatio: '1',
    zIndex: 0,
    background: theme => {
      // The tinted arc sits on a solid paper layer, so the bright resting ring underneath cannot
      // wash it out.
      const base = alpha(theme.palette.primary.main, 0.18);
      const _theme$palette$primar = theme.palette.primary,
        light = _theme$palette$primar.light,
        dark = _theme$palette$primar.dark;
      return "conic-gradient(from 0deg, ".concat(base, " 0deg, ").concat(light, " 50deg, ").concat(dark, " 100deg, ").concat(base, " 150deg, ").concat(base, " 360deg), ").concat(theme.palette.background.paper);
    },
    opacity: thinking ? 1 : 0,
    transform: 'translate(-50%, -50%)',
    animation: 'livySpin 1.6s linear infinite',
    animationPlayState: thinking ? 'running' : 'paused'
  },
  '@keyframes livySpin': {
    from: {
      transform: 'translate(-50%, -50%) rotate(0deg)'
    },
    to: {
      transform: 'translate(-50%, -50%) rotate(360deg)'
    }
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::before': {
      animation: 'none'
    }
  }
});
export const COMPOSER_INNER = {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  minHeight: 60,
  p: '10px 10px 10px 12px',
  borderRadius: '28.5px',
  bgcolor: 'background.paper',
  cursor: 'text'
};

/**
 * Separates one exchange from the next. Without it every turn carried the same weight and a long
 * transcript read as one undifferentiated column.
 */
export const EXCHANGE_START = {
  mt: '34px',
  pt: '30px',
  borderTop: 1,
  borderColor: 'divider'
};

/** The transcript scrolls under the composer rather than stopping dead against it. */
export const COMPOSER_FADE = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 28,
  pointerEvents: 'none',
  background: theme => "linear-gradient(to bottom, transparent, ".concat(theme.palette.background.paper, ")")
};

/** Marks the live edge of a streaming reply, so a pause reads as thinking rather than as finished. */
export const CARET = {
  display: 'inline-block',
  width: '2px',
  height: '1em',
  ml: '2px',
  verticalAlign: '-2px',
  bgcolor: 'primary.main',
  animation: 'livyBlink 1s steps(2, start) infinite',
  '@keyframes livyBlink': {
    '50%': {
      opacity: 0
    }
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none'
  }
};
export const PILL = {
  textTransform: 'none',
  fontSize: 12.5,
  fontWeight: 400,
  lineHeight: '18px',
  minHeight: 26,
  py: '3px',
  px: '11px',
  borderRadius: '13px',
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover'
  }
};